import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ResmoMascot from "../components/ResmoMascot";
import ProMoInstructions from "../components/ProMoInstructions";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useCopy } from "../lib/copy";
import { Upload, FileText, ArrowRight, RefreshCw, Wand2 } from "lucide-react";
import FirstImpressionCard from "../components/FirstImpressionCard";

export default function FeedResume() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { isPro } = useTheme();
  const t = useCopy();
  const [mode, setMode] = useState("upload"); // upload | paste
  const [text, setText] = useState("");
  const [filename, setFilename] = useState("");
  const [chewing, setChewing] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [existing, setExisting] = useState(null); // existing base resume
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/resume/base");
        if (data && data.content) {
          setExisting(data);
          setText(data.content);
          setFilename(data.source_filename || "saved");
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const triggerFile = () => fileRef.current?.click();

  const onFileSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError("");
    setChewing(true);
    setDone(false);
    setFilename(f.name);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/resume/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setText(data.content);
      // Keep the chew animation on screen for at least 1.4s for delight
      setTimeout(() => {
        setChewing(false);
        setDone(true);
        refresh();
      }, 1400);
    } catch (err) {
      setChewing(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onSavePasted = async () => {
    setError("");
    if (text.trim().length < 30) {
      setError("Resume looks too short.");
      return;
    }
    setChewing(true);
    setDone(false);
    try {
      const { data } = await api.post("/resume/text", { content: text });
      setText(data.content);
      setFilename("pasted_text");
      setTimeout(() => {
        setChewing(false);
        setDone(true);
        refresh();
      }, 1400);
    } catch (err) {
      setChewing(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const monsterState = chewing ? "chewing" : done ? "satisfied" : existing ? "idle" : "hungry";

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="mb-8">
          <div className="rm-tag mb-3">Step 1 / 2</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase">
            {t(<>Feed me your <span className="rm-text-orange">resume.</span></>, <>Upload your <span className="rm-text-orange">resume.</span></>)}
          </h1>
          <p className="text-base md:text-lg mt-2 max-w-2xl" style={{ color: "#475569" }}>
            {t(
              "One bite is all I need. I'll remember it forever and use it as the foundation for every tailored resume you ask for.",
              "Upload it once. Resmo stores it as the foundation for every tailored output."
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Monster / Instructions card */}
          <div className="lg:col-span-5">
            {isPro ? (
              <ProMoInstructions
                fullHeight
                tag={done ? "Saved" : existing ? "Resume on file" : "Step 1 of 2"}
                title={
                  done
                    ? "Resume saved."
                    : existing
                    ? "Replace or continue."
                    : "Upload your resume."
                }
                intro={
                  done
                    ? "Your resume is the foundation for every tailored output. Continue to step 2."
                    : "Resmo stores one master resume per account. We use it for every analysis you run."
                }
                steps={
                  done
                    ? [
                        "Click 'Continue to job description' to start your first analysis.",
                        "Or replace this resume any time — past analyses keep referencing the version they were built from.",
                      ]
                    : [
                        "Upload a PDF, DOCX, or TXT — max 5MB.",
                        "Or paste plain text directly into the panel.",
                        "Resmo extracts skills, experience, and education automatically.",
                      ]
                }
                footnote={
                  done
                    ? `Last upload: ${filename || "—"}`
                    : "Your resume is private and never shared without your explicit action."
                }
                testId="promo-feed-resume-instructions"
              />
            ) : (
              <div
                className="rm-card-static p-8 flex flex-col items-center justify-center h-full"
                style={{ background: chewing ? "#FFC145" : done ? "#ECFDF5" : "#FFF2EC" }}
                data-testid="feed-resume-monster-card"
              >
                <ResmoMascot state={monsterState} size={240} />
                <div className="font-display text-2xl mt-4 text-center uppercase">
                  {chewing
                    ? "OM NOM NOM..."
                    : done
                    ? "DELICIOUS!"
                    : existing
                    ? "Already snacked. Re-feed?"
                    : "Hungry for paper..."}
                </div>
                <div className="font-mono-rm text-xs mt-2 uppercase tracking-widest text-center">
                  {filename ? `last bite: ${filename}` : "no resume yet"}
                </div>

                {done && (
                  <>
                    <FirstImpressionCard className="mt-5 text-left" />
                    <button
                      onClick={() => navigate("/feed-job")}
                      className="rm-btn rm-btn-primary mt-6"
                      data-testid="proceed-to-job-btn"
                    >
                      Now feed the job <ArrowRight size={18} />
                    </button>
                  </>
                )}
              </div>
            )}
            {isPro && done && (
              <button
                onClick={() => navigate("/feed-job")}
                className="rm-btn rm-btn-primary mt-4 w-full"
                data-testid="proceed-to-job-btn"
              >
                Continue to job description <ArrowRight size={18} />
              </button>
            )}
          </div>

          {/* Input card */}
          <div className="lg:col-span-7">
            <div className="rm-card-static p-6 md:p-8">
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setMode("upload")}
                  className={`rm-btn ${mode === "upload" ? "rm-btn-dark" : "rm-btn-ghost"} text-xs px-4 py-2`}
                  data-testid="mode-upload-btn"
                >
                  <Upload size={14} /> Upload file
                </button>
                <button
                  onClick={() => setMode("paste")}
                  className={`rm-btn ${mode === "paste" ? "rm-btn-dark" : "rm-btn-ghost"} text-xs px-4 py-2`}
                  data-testid="mode-paste-btn"
                >
                  <FileText size={14} /> Paste text
                </button>
              </div>

              {mode === "upload" ? (
                <div
                  onClick={triggerFile}
                  className="cursor-pointer text-center py-12 rounded-2xl"
                  style={{
                    border: "4px dashed #0F1B2D",
                    background: chewing ? "#FFC145" : "#FFF2EC",
                  }}
                  data-testid="upload-dropzone"
                >
                  <input
                    type="file"
                    ref={fileRef}
                    accept=".pdf,.docx,.txt"
                    className="hidden"
                    onChange={onFileSelected}
                    data-testid="upload-file-input"
                  />
                  <Upload size={32} className="mx-auto mb-3" />
                  <div className="font-display text-xl uppercase">
                    {chewing ? t("Chewing...", "Processing...") : t("Drop or click to upload", "Drop or click to upload")}
                  </div>
                  <div className="font-mono-rm text-xs mt-2 uppercase tracking-widest" style={{ color: "#475569" }}>
                    PDF · DOCX · TXT · max 5MB
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <label className="rm-label block">Paste your resume content</label>
                  <textarea
                    className="rm-textarea"
                    placeholder="Copy-paste your full resume here..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    data-testid="paste-textarea"
                  />
                  <button
                    onClick={onSavePasted}
                    disabled={chewing || text.trim().length < 30}
                    className="rm-btn rm-btn-primary"
                    data-testid="save-pasted-btn"
                  >
                    {chewing ? t("Chewing...", "Saving...") : t("Feed it to me", "Save resume")}
                  </button>
                </div>
              )}

              {error && (
                <div className="mt-4 p-3 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }} data-testid="feed-resume-error">
                  {error}
                </div>
              )}

              {!existing && !chewing && !done && (
                <div
                  className="mt-6 p-4 rounded-xl flex flex-wrap items-center justify-between gap-3"
                  style={{ border: "3px dashed #0F1B2D", background: "#FFF7ED" }}
                  data-testid="feed-resume-builder-cta"
                >
                  <div className="flex-1 min-w-[200px]">
                    <div className="rm-label">No resume yet?</div>
                    <div className="text-sm">
                      Build a clean one from scratch in 6 quick steps.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/resume-builder")}
                    className="rm-btn rm-btn-secondary text-xs px-3 py-2"
                    data-testid="open-resume-builder-btn"
                  >
                    <Wand2 size={14} /> Build from scratch
                  </button>
                </div>
              )}

              {existing && !chewing && (
                <div className="mt-6 p-4 rounded-xl" style={{ border: "3px solid #0F1B2D", background: "#ECFDF5" }} data-testid="existing-resume-banner">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="rm-label">Saved resume</div>
                      <div className="font-bold text-sm">{existing.source_filename || "pasted_text"}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate("/feed-job")}
                        className="rm-btn rm-btn-primary text-xs px-4 py-2"
                        data-testid="use-saved-btn"
                      >
                        Use this & continue <ArrowRight size={14} />
                      </button>
                      <button
                        onClick={() => {
                          setExisting(null);
                          setText("");
                          setFilename("");
                          setMode("upload");
                        }}
                        className="rm-btn rm-btn-ghost text-xs px-4 py-2"
                        data-testid="replace-resume-btn"
                      >
                        <RefreshCw size={14} /> Replace
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
